<template>
  <div v-if="!buildError" id="c-changelog-modal">
    <vp-modal :styles="widgetStyles">
      <template #header>{{ title }}</template>
      <section id="c-changelog-modal__body">
        <div id="c-changelog-modal__description">
          <slot>
            <p>{{description}}</p>
          </slot>
        </div>
        <div id="c-changelog-modal__content">
          <vp-table id="c-changelog-modal__table" ref="table" v-bind="tableProps" @selection-updated="(selected, opts) => handleChanges(selected, opts)">
            <template #summary>
              <div ref='header' id="c-changelog-modal__table-header">
                <span>{{ subTitle }}</span>
                <vp-pill v-if="enableCounter">{{ counter }}</vp-pill>
              </div>
            </template>
            <template #cell="{ column, row }">
              <div v-if="column.id === 'key' && row.groupId" class="c-changelog-modal__row-key">
                <span class="c-changelog-modal__row-key__group">[{{ row.groupId }}]</span>
                <span class="c-changelog-modal__row-key__text">{{ row.key }}</span>
              </div> 
            </template>
          </vp-table>
        </div>
      </section>
      <template #footer>
        <section class="vp-list-controls">
          <vp-button ref="cancelBtn" class="no-fill medium blue" @click="close()">Cancel</vp-button>
          <vp-button v-if="hasData" ref="submitBtn" class="medium" @click="submit()">Confirm</vp-button>
        </section>
      </template>
    </vp-modal>
  </div>
</template>

<script>
import VpButton from '../vp-button/VpButton.vue';
import VpTable from '../vp-table/VpTable.vue';
import VpModal from '../vp-modal/VpModal.vue';
import VpPill from '../vp-pill/VpPill.vue';

export default {
  name: 'VpChangelogModal',
  inject: ['close'],
  props: {
    description: { 
      type: String, 
      default: 'Are you sure you want to submit the following changes? To submit changes, click the “Confirm” button below. To continue making changes, click the “Cancel” button.'
    },
    title: { type: String, default: 'Confirm Changes' },
    subTitle: { type: String, default: 'Changelog' },
    enableCounter: { type: Boolean, default: true },
    styles: { type: Object, default: () => ({}) },
    /** Handler Props */
    filter: { type: Function, default: null },
    method: { type: Function, required: true },
    /** Data Properties */
    changes: { type: Array, default: null },
    dataKey: { type: String, default: null },
    modified: { type: [Array, Object], default: null },
    original: { type: [Array, Object], required: true },
  },
  components: {
    VpButton, VpTable, VpModal, VpPill,
  },
  data: () => ({
    buildError: false,
    counter: 0,
    selected: [],
    newData: [],
    newDataSets: [],
    hasChanges: false,
  }),
  computed: {
    hasData() {
      return Boolean(this.data.length);
    },
    data() {
      const data = this.changes || this.compileDifferences();
      return this.parseDataSet(data);
    },
    log() {
      return this.data.map(item => {
        try {
          return {
            ...item, 
            selected: Boolean(this.selected.find((opt) => JSON.stringify(opt) === JSON.stringify(item))),
          }
        } catch (err) {
          return item;
        }
      });
    },
    isArray() {
      return Array.isArray(this.original);
    },
    tableColumns() {
      return [
        { id: 'key', label: 'Key', align: 'left', title: 'Data Key' },
        { id: 'previous', label: 'Old', align: 'left', title: 'Original Value' },
        { id: 'current', label: 'New', align: 'right', title: 'Current Value' },
      ];
    },
    tableProps() {
      const { $refs, data: rowData, tableColumns: columns } = this;
      const props = {
        columns,
        rowData,
        allowRowSelection: 1,
        emptyStateMessage: { title: 'No changes found', description: 'Please make some changes, then try again' },
        itemsPerPage: 100,
        selectAllOverride: true,
        showFilters: 0,
        showSearch: 0,
        showFooter: 1,
        stickyHeader: true,
        stickyTarget: { $refs, key: 'header' },
        styles: { toolsTopOffset: 0 },
      }
      return props;
    },
    widgetStyles() {
      return {
        bodyPadding: '0',
        width: '900px',
        overflow: 'auto',
        headerBorder: '1px solid #DBDBDB',
        headerHeight: '78px',
        headerPadding: '1.5rem 1.25rem',
        buttonsPadding: '1.5rem 0 2.25rem',
        buttonsAlign: 'flex-start',
        ...this.styles,
      }
    },  
  },
  updated() {
    return this.init();
  },
  created() {
    return this.checkProps();
  },
  mounted() {
    return this.init();
  },
  methods: {
    checkProps() {
      if (!this.changes && !this.modified) {
        const title = `
          Error: Missing required prop to compile changelog, 
          must attach either pre-parsed data via "changes" prop, 
          or a modified object/array via "modified" prop
        `;
        const body = `
          Received: 
            "changes": ${this.changes},
            "modified": ${this.modified},
        `;
        this.buildError = true;
        return console.error(title, body);
      }
      this.buildError = false;
    },
    parseDataSet(data = []) {
      const sets = {};
      const newData = data.map((item) => {
        if (item.groupId) {
          const refId = `${item.groupId}-${item.key}`
          if (sets[item.groupId]) sets[item.groupId].push(refId);
          else sets[item.groupId] = [refId];
          return { ...item, refId }
        } 
        return item;
      }, {});
      return newData.map(item => {
        if (!item.groupId) return item;
        const siblings = sets[item.groupId].filter((sibling) => sibling !== item.refId)
        return { ...item, $group: { siblings, refId: item.refId } };
      });
    },
    init() {
      if (!this.buildError) {
        /** toggles VpTable checkboxes, sets all data[field].selected to true */
        return this.$refs.table ? this.$refs.table.selectAllRowsClicked(true) : null;
      }
    },
    stringify(data) {
      try {
        return (['boolean', 'string'].includes(typeof data)) ? data : JSON.stringify(data);
      } catch (error) {
        console.error('Error while parsing data to string', error.message);
        return data;
      }
    },
    compileDifferences() {
      const changes = [];
      if (this.isArray) {
        if (!this.dataKey) console.error('ChangelogModal: Missing required prop "dataKey" when parsing Arrays');
        else this.modified.forEach((item, index) => {
          const originalField = this.original[index] || {};
          const current = this.stringify(item[this.dataKey]);
          const previous = this.stringify(originalField[this.dataKey]);
          const data = { key: item.key, current: item[this.dataKey], previous: originalField[this.dataKey] };
          if (item.groupId) data.groupId = item.groupId;
          if (current !== previous) {
            if (this.filter && !this.filter(item)) return;
            changes.push(data);
          }
        });
      } else for (const key in this.modified) {
        const current = this.stringify(this.modified[key]);
        const previous = this.stringify(this.original[key]);
        if (current !== previous) {
          if (this.filter && !this.filter(key)) continue;
          changes.push({ key, current: this.modified[key], previous: this.original[key] });
        }
      }
      return changes;
    },
    parseSelected(selected, opts) {
      const { table } = this.$refs;
      const { index, event } = (opts || {});
      if (!table || !opts) return selected;
      const { group } = (table?.rowState[index] || {});
      /** IF row is Group Linked, sync toggle with relatives */
      if (group && group?.siblings?.length) {
        for (const relative of group.siblings) {
          /** Skip if relative is target */
          if (relative === group.refId) continue;
          /** Update relative if found by matching groupId */
          const relativeIndex = table?.rowState.findIndex(({ group }) => group?.refId === relative);
          if (relativeIndex !== -1) table.rowState[relativeIndex].selected = event.value;
        }
      }

      return group ? table.selectedRows : selected;
    },
    handleChanges(_selected = [], opts) {
      const selected = this.parseSelected(_selected, opts);
      this.selected = selected;
      this.counter = selected.length;
      /** Deconstruct original data */
      const original = this.original || (this.isArray ? [] : {});
      const modified = this.modified || (this.isArray ? [] : {});
      /** Strict payload for only new changes */
      this.newData = this.isArray ? [] : {};
      /** Real payload for all new and original values */
      this.newDataSets = this.isArray ? [] : {};
      this.hasChanges = Boolean(selected.length);

      if (this.isArray) {
        const condensed = [...original, ...modified].reduce((fields, field) => {
          const exist = fields.find(({ key }) => key === field.key);
          if (!exist) fields.push(field);
          return fields;
        }, []);
        condensed.forEach((data) => {
          const { current } = selected.find((item) => item.key === data.key) || {};
          const fallback = this.data.find((field) => field.key === data.key)
          /** If item is selected, use new value */
          if (current !== undefined) this.newData.push({ key: data.key, value: current });
          else if (fallback && fallback.previous !== undefined) this.newData.push({ key: data.key, value: data.value });

          if (current !== undefined) this.newDataSets.push({ key: data.key, value: current });
          else this.newDataSets.push({ key: data.key, value: fallback ? fallback?.previous : data.value });
        });
      } else {
        Object.entries({ ...modified, ...original }).forEach(([key, value]) => {
          let { current } = selected.find((item) => item.key === key) || {};
          const fallback = this.data.find((field) => field.key === key);
          /** If item is selected, use new value */
          if (current !== undefined) this.newData[key] = current;
          else if (fallback && fallback.previous !== undefined) this.newData[key] = fallback?.previous;
          
          if (current !== undefined) this.newDataSets[key] = current;
          else this.newDataSets[key] = fallback ? fallback?.previous : value;
        });
      }
    },
    async submit() {
      const { isArray, hasChanges, newData, newDataSets, log } = this;
      const changelog = { newData, newDataSets, hasChanges, isArray, log };
      await this.method(changelog);
      return this.close();
    },
  },
};
</script>

<style lang="scss">
@import "../../styles/mixins.scss";

#c-changelog-modal {
  &__description {
    background: white;
    border-bottom: 1px solid #DBDBDB;
    & > * {
      margin: 0;
      line-height: 1.5rem;
    }
  }
  &__body {
    margin: 0;
    background: #f4f7f8;

    & > * {
      padding: 1.25rem;
    }
  }

  &__table-header {
    top: 0;
    z-index: 4;
    gap: .75rem;
    height: 76px;
    display: flex;
    color: #4f4f4f;
    position: sticky;
    font-size: 1.5rem;
    padding: 0 1.25rem;
    background: white;
    align-items: center;
    box-sizing: border-box;
    border-radius: 10px 10px 0 0;
    border-bottom: 1px solid #dbdbdb;
  }

  .c-changelog-modal__row-key {
    display: flex;
    &__group {
      font-size: .5rem;
    }
  }
  .vp-list-controls {
    margin: 0;
    gap: 1.25rem;
    display: flex;
    justify-content: center;
    & > * {
      margin: 0;
    }
  }

  /** VCL Table overrides */
  &__tableContainer {
    max-height: 40vh;
    overflow-y: auto;
    border-radius: 10px 10px;

    table {
      border-top: none;
    }

    thead {
      /** Sticky table header when scrolling table content */
      background-color: white;
    }

    tr {
      /** Allow text in columns to wrap instead of overflow - expanding height of row */
      overflow-wrap: anywhere;
    }

    .vcl-table {
      &__row {
        & > td:nth-child(4) {
          /** New Cell Color */
          background-color: #e6f6ed;
          overflow-wrap: break-word;
        }

        & > td:nth-child(3) {
          /** Old Cell Color */
          background-color: #fde5e6;
          overflow-wrap: break-word;
        }
      }

      &__results {
        /** Remove odd margin throwing off centered table footer content */
        margin: 0;
      }

      &__footer {
        gap: 1rem;
        align-items: center;
      }

      &__pagination-controls {
        /** Sticky pagination when scrolling table content */
        bottom: 0;
        position: sticky;
        background: white;
      }
      &__empty-state-content {
        padding: 0;
      }
    }
  }
}
</style>
